import { Id } from '@nextforce/api'
import {
  QueryFunctionContext,
  QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query'
import { t } from 'app/i18l'
import { useServer } from 'app/server/context'
import { BaseHomeItem, HomeItem, SummaryItem } from 'home/types'
import {
  makeErrorHomeItem,
  makeSuccessHomeItem,
} from 'home/update_app/shared/utils'
import { RetryQuery } from 'shared/queries/utils'
import { useUser } from 'users/queries/detail'
import { convertNotifications } from './shared/convert'
import keys from './shared/keys'
import * as Response from './shared/response'
import { NotificationsPage } from './shared/types'
import { useNotificationsSummary } from './summary'

type PageParam = NotificationsPage['nextPageUri']

export function useNotifications(retryQuery?: RetryQuery, storeId?: Id) {
  const { data: user } = useUser()
  const { server } = useServer()

  return useInfiniteQuery({
    queryKey: storeId ? keys.list(storeId) : keys.all,
    queryFn: ({
      pageParam: nextPageUri,
    }: QueryFunctionContext<QueryKey, PageParam>) =>
      fetchNotifications(nextPageUri),
    initialPageParam: undefined as PageParam,
    enabled: !!user,
    retry: retryQuery ?? false,
    getNextPageParam: page => page.nextPageUri,
    refetchOnMount: true,
  })

  async function fetchNotifications(
    nextPageUri: PageParam
  ): Promise<NotificationsPage> {
    const url =
      nextPageUri ??
      (user?.role === 'helper'
        ? '/v2/helpers/me/notifications'
        : storeId
        ? `/v2/managers/me/notifications?store_id=${storeId}`
        : '/v2/managers/me/notifications')

    return convertNotifications(await server.get<Response.Notifications>(url))
  }
}

export function useNotificationsSummaryHomeItem(): HomeItem | undefined {
  const { data: user } = useUser()
  const enabled = user?.permissions?.canSeeNotifications
  const {
    data: notificationsSummaryData,
    isError: isNotificationsSummaryError,
    error: notificationsSummaryError,
  } = useNotificationsSummary()

  if (!enabled) {
    return
  }

  const baseItem = {
    title: t('notifications'),
    route: { name: 'notifications' },
    color: 'black',
    backgroundColor: '#C7DBDF',
  } as BaseHomeItem

  if (isNotificationsSummaryError) {
    return makeErrorHomeItem(baseItem, notificationsSummaryError)
  }

  const summaryItems: SummaryItem[] =
    notificationsSummaryData?.notifications?.map(notification => ({
      title: notification.title,
      description: notification.message,
    })) ?? []

  return makeSuccessHomeItem(
    baseItem,
    summaryItems,
    notificationsSummaryData?.unreadCount
  )
}
